import { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import useAuth from 'src/hooks/useAuth';
import { useDispatch } from 'src/redux/store';
import {
    getMensagensWhatsappDb,
    setChatInfo,
    setLimparChatInfo,
} from 'src/redux/slices/chat';
import v2_actionBuscarChatById from 'src/actions/v2/services/chats/v2_actionBuscarChatById';
import v2_actionCtwaAnuncioChats from 'src/actions/v2/dashboard/relatorios/v2_actionCtwaAnuncioChats';
import DrawerChatWhatsapp from 'src/sections/@chats/chat/DrawerChatWhatsapp';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from 'src/@/components/ui/sheet';

import { MKT_CTWA_DESIGN as d } from 'src/pages/dashboard/Marketing/MarketingCtwa.design';
import { fmtInt } from 'src/sections/@dashboard/home/metricas/MetricasUI';
import { formatarTelefone, montarQuerysCtwaAnuncioChats, nomeAnuncio } from './ctwa.utils';
import CtwaAnuncioThumb from './CtwaAnuncioThumb';

const CHATS_PAGE_SIZE = 25;

export default function CtwaAnuncioChatsDrawer({ open, onOpenChange, anuncio, querysBase }) {
    const { t } = useTranslation('marketing');
    const dispatch = useDispatch();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [lista, setLista] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [chat, setChat] = useState(null);
    const [openChatDetalhes, setOpenChatDetalhes] = useState(false);
    const [loadingVerDetalhes, setLoadingVerDetalhes] = useState(false);

    async function handleOpenDetails(card) {
        if (!card?._id || loadingVerDetalhes) return;

        setLoadingVerDetalhes(true);
        dispatch(setLimparChatInfo());
        setOpenChatDetalhes(true);

        try {
            const res = await v2_actionBuscarChatById(card._id, null, user.id);
            setChat(res.data);
            dispatch(setChatInfo(res.data));
            await new Promise((resolve) => setTimeout(resolve, 100));
            dispatch(getMensagensWhatsappDb(res.data.sessao, res.data.numeroCliente));
        } catch (err) {
            console.log('CtwaAnuncioChatsDrawer handleOpenDetails', err);
            setChat(null);
            setOpenChatDetalhes(false);
        } finally {
            setLoadingVerDetalhes(false);
        }
    }

    function handleCloseChat() {
        setChat(null);
        setOpenChatDetalhes(false);
    }

    const carregarChats = useCallback(
        async (pageToLoad, append = false) => {
            if (!anuncio?.source_id) return;

            if (append) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }

            try {
                const querys = montarQuerysCtwaAnuncioChats({
                    querysBase,
                    sourceId: anuncio.source_id,
                    page: pageToLoad,
                    limit: CHATS_PAGE_SIZE,
                });
                const res = await v2_actionCtwaAnuncioChats(querys);
                const payload = res.data.data || {};
                const novos = payload.chats || [];

                setTotal(payload.total ?? 0);
                setHasMore(!!payload.hasMore);
                setPage(payload.page ?? pageToLoad);
                setLista((prev) => (append ? [...prev, ...novos] : novos));
            } catch (err) {
                console.log('CtwaAnuncioChatsDrawer', err);
                if (!append) {
                    setLista([]);
                    setTotal(0);
                    setHasMore(false);
                }
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        [anuncio?.source_id, querysBase]
    );

    useEffect(() => {
        if (!open || !anuncio?.source_id) return undefined;

        setPage(1);
        setTotal(0);
        setHasMore(false);
        setLista([]);
        carregarChats(1, false);

        return undefined;
    }, [open, anuncio?.source_id, querysBase, carregarChats]);

    const titulo = anuncio ? nomeAnuncio(anuncio) : '';

    return (
        <>
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent className="w-full sm:max-w-lg p-0 flex flex-col">
                    <SheetHeader className="px-5 py-4 border-b border-[#eeeef2] text-left">
                        <SheetTitle className="text-[16px] font-extrabold text-[#17172b]">{t('ctwaPage.chatsDrawer.title')}</SheetTitle>
                        {anuncio && (
                            <div className="flex items-center gap-3 mt-3">
                                <CtwaAnuncioThumb anuncio={anuncio} titulo={titulo} size="sm" />
                                <div className="min-w-0">
                                    <p className="text-[13px] font-bold text-[#17172b] line-clamp-2">{titulo}</p>
                                    <p className="text-[11px] text-[#8b8b9a] font-mono mt-0.5 truncate">{anuncio.source_id}</p>
                                </div>
                            </div>
                        )}
                    </SheetHeader>

                    <div className="flex-1 overflow-y-auto px-5 py-3">
                        {loading ? (
                            <p className="text-[13px] text-[#8b8b9a] py-8 text-center">{t('common.loading')}</p>
                        ) : !lista.length ? (
                            <p className="text-[13px] text-[#8b8b9a] py-8 text-center">{t('ctwaPage.chatsDrawer.empty')}</p>
                        ) : (
                            <div className="flex flex-col">
                                {lista.map((item, index) => {
                                    const chatItem = item.chat;
                                    const nome = chatItem?.nomeCliente || t('ctwaPage.chatsDrawer.noName');
                                    const podeAbrir = !!chatItem?._id;
                                    const telefone = chatItem?.numeroCliente || item.phone;

                                    return (
                                        <button
                                            key={`${item.phone}-${chatItem?._id || index}`}
                                            type="button"
                                            disabled={!podeAbrir || loadingVerDetalhes}
                                            onClick={() => podeAbrir && handleOpenDetails(chatItem)}
                                            className={`${d.chatDrawerRow} ${podeAbrir ? 'cursor-pointer hover:bg-[#fafafb]' : 'opacity-70 cursor-default'}`}
                                        >
                                            <div className="flex-1 min-w-0 text-left">
                                                <p className="text-[13px] font-bold text-[#17172b] truncate">{nome}</p>
                                                <p className="text-[11px] text-[#8b8b9a] mt-0.5 tabular-nums">
                                                    {formatarTelefone(telefone)}
                                                    {chatItem?.statusRoom ? ` · ${chatItem.statusRoom}` : ''}
                                                </p>
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                                <p className="text-[13px] font-bold tabular-nums text-[#17172b]">{fmtInt(item.entradas)}</p>
                                                <p className="text-[10px] text-[#a0a0af] uppercase tracking-wide">
                                                    {item.ultimaEntrada ? moment(item.ultimaEntrada).format('DD/MM HH:mm') : '—'}
                                                </p>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {!loading && lista.length > 0 && (
                        <div className="border-t border-[#eeeef2] px-5 py-3 flex-shrink-0">
                            <p className="text-[11px] text-[#8b8b9a] text-center mb-2">
                                {t('ctwaPage.chatsDrawer.showing', { count: lista.length, total: fmtInt(total) })}
                            </p>
                            {hasMore && (
                                <button
                                    type="button"
                                    disabled={loadingMore}
                                    onClick={() => carregarChats(page + 1, true)}
                                    className="w-full py-2.5 text-[13px] font-semibold text-[#7C3AED] hover:bg-violet-50 rounded-xl transition-colors disabled:opacity-50"
                                >
                                    {loadingMore ? t('ctwaPage.chatsDrawer.loadingMore') : t('ctwaPage.chatsDrawer.loadMore')}
                                </button>
                            )}
                        </div>
                    )}
                </SheetContent>
            </Sheet>

            {chat && (
                <DrawerChatWhatsapp
                    chat={chat}
                    open={openChatDetalhes}
                    onClose={handleCloseChat}
                    source="chat.supervisionar"
                />
            )}
        </>
    );
}
